import React from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Plus, Loader2, Monitor, Wifi, Projector, Phone, Settings2, X, Home, Ruler, Sparkles,
} from 'lucide-react';
import { FileDropzone } from '../../components/ui/file-dropzone';
import { Switch } from '../../components/ui/switch';

const ROOM_COLORS = [
  { value: '#3B82F6', label: 'Blau' },
  { value: '#10B981', label: 'Grün' },
  { value: '#F59E0B', label: 'Gelb' },
  { value: '#EF4444', label: 'Rot' },
  { value: '#8B5CF6', label: 'Lila' },
  { value: '#EC4899', label: 'Pink' },
  { value: '#6366F1', label: 'Indigo' },
  { value: '#14B8A6', label: 'Teal' },
];

export const EQUIPMENT_OPTIONS = [
  { value: 'monitor', label: 'Monitor', icon: Monitor },
  { value: 'wifi', label: 'WLAN', icon: Wifi },
  { value: 'projector', label: 'Beamer', icon: Projector },
  { value: 'phone', label: 'Telefon', icon: Phone },
];

export const ROOM_TYPES = {
  'besprechung': 'Besprechungsraum',
  'schulung': 'Schulungsraum',
  'buero': 'Büro',
  'werkstatt': 'Werkstatt',
  'lager': 'Lager',
  'sonstiges': 'Sonstiges',
};

export const RoomFormDialog = ({
  open, onOpenChange, editingRoom, roomForm, setRoomForm,
  onSave, saving, locations,
  onOwnershipUpload, uploadingOwnership = false,
}) => {
  const toggleEquipment = (value) => {
    setRoomForm((prev) => ({
      ...prev,
      equipment: prev.equipment.includes(value)
        ? prev.equipment.filter((e) => e !== value)
        : [...prev.equipment, value],
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">
            {editingRoom ? 'Raum bearbeiten' : 'Neuen Raum erstellen'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-4">
          <div className="space-y-2">
            <Label htmlFor="room-name">Raumname *</Label>
            <Input
              id="room-name"
              value={roomForm.name}
              onChange={(e) => setRoomForm((prev) => ({ ...prev, name: e.target.value }))}
              placeholder="z.B. Besprechungsraum 1"
              data-testid="room-name-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="room-description">Beschreibung</Label>
            <Textarea
              id="room-description"
              value={roomForm.description}
              onChange={(e) => setRoomForm((prev) => ({ ...prev, description: e.target.value }))}
              placeholder="Optionale Beschreibung..."
              rows={2}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Standort</Label>
              <Select
                value={roomForm.location_id || "none"}
                onValueChange={(v) => setRoomForm((prev) => ({ ...prev, location_id: v === "none" ? "" : v }))}
              >
                <SelectTrigger><SelectValue placeholder="Optional" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Kein Standort</SelectItem>
                  {locations.map((loc) => (
                    <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="room-capacity">Kapazität</Label>
              <Input
                id="room-capacity" type="number" min={1}
                value={roomForm.capacity}
                onChange={(e) => setRoomForm((prev) => ({ ...prev, capacity: parseInt(e.target.value) || 1 }))}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Farbe</Label>
            <div className="flex flex-wrap gap-2">
              {ROOM_COLORS.map((color) => (
                <button
                  key={color.value} type="button"
                  className={`w-8 h-8 rounded-full border-2 transition-transform ${
                    roomForm.color === color.value ? 'border-foreground scale-110' : 'border-transparent'
                  }`}
                  style={{ backgroundColor: color.value }}
                  onClick={() => setRoomForm((prev) => ({ ...prev, color: color.value }))}
                  title={color.label}
                />
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label>Ausstattung</Label>
            <div className="flex flex-wrap gap-2">
              {EQUIPMENT_OPTIONS.map((eq) => {
                const Icon = eq.icon;
                return (
                  <Button
                    key={eq.value} type="button"
                    variant={roomForm.equipment.includes(eq.value) ? 'default' : 'outline'}
                    size="sm" onClick={() => toggleEquipment(eq.value)}
                  >
                    <Icon className="h-4 w-4 mr-1" />{eq.label}
                  </Button>
                );
              })}
            </div>
            {/* Custom equipment entries */}
            <div className="flex flex-wrap gap-1.5 mt-1">
              {roomForm.equipment
                .filter(eq => !EQUIPMENT_OPTIONS.some(o => o.value === eq))
                .map(eq => (
                  <span key={eq} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-secondary border">
                    {eq}
                    <button type="button" onClick={() => toggleEquipment(eq)} className="hover:text-destructive">
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
            </div>
            <div className="flex gap-2 mt-1">
              <Input
                placeholder="Eigene Ausstattung hinzufügen..."
                className="text-sm h-8"
                data-testid="custom-equipment-input"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    const val = e.target.value.trim();
                    if (val && !roomForm.equipment.includes(val)) {
                      setRoomForm((prev) => ({ ...prev, equipment: [...prev.equipment, val] }));
                      e.target.value = '';
                    }
                  }
                }}
              />
              <Button type="button" variant="outline" size="sm" className="h-8 px-2"
                onClick={() => {
                  const input = document.querySelector('[data-testid="custom-equipment-input"]');
                  const val = input?.value?.trim();
                  if (val && !roomForm.equipment.includes(val)) {
                    setRoomForm((prev) => ({ ...prev, equipment: [...prev.equipment, val] }));
                    input.value = '';
                  }
                }}
              >
                <Plus className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>

          {/* Erweiterte Felder */}
          <div className="border-t border-border/40 pt-4 mt-4">
            <h4 className="font-medium text-sm mb-3 flex items-center gap-2">
              <Settings2 className="h-4 w-4" />Erweiterte Einstellungen
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="room-type">Raumtyp</Label>
                <Input
                  id="room-type"
                  list="room-type-suggestions"
                  value={roomForm.room_type || ''}
                  onChange={(e) => setRoomForm((prev) => ({ ...prev, room_type: e.target.value }))}
                  placeholder="z.B. Besprechungsraum, Werkstatt..."
                  data-testid="room-type-input"
                />
                <datalist id="room-type-suggestions">
                  {Object.values(ROOM_TYPES).map((label) => (
                    <option key={label} value={label} />
                  ))}
                </datalist>
              </div>
              <div className="space-y-2">
                <Label htmlFor="room-building">Gebäude</Label>
                <Input id="room-building" value={roomForm.building}
                  onChange={(e) => setRoomForm((prev) => ({ ...prev, building: e.target.value }))}
                  placeholder="z.B. Hauptgebäude" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="room-floor">Etage</Label>
                <Input id="room-floor" value={roomForm.floor}
                  onChange={(e) => setRoomForm((prev) => ({ ...prev, floor: e.target.value }))}
                  placeholder="z.B. EG, 1. OG" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="room-rate">Stundenpreis (Euro)</Label>
                <Input id="room-rate" type="number" step="0.01" min="0"
                  value={roomForm.hourly_rate}
                  onChange={(e) => setRoomForm((prev) => ({ ...prev, hourly_rate: e.target.value }))}
                  placeholder="Optional" />
              </div>
            </div>

            {/* Reinigung */}

            {/* Größe + Reinigungspreis + Nachweis-Typ */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="room-size" className="flex items-center gap-1">
                  <Ruler className="h-3.5 w-3.5" />
                  Größe (m²)
                </Label>
                <Input id="room-size" type="number" step="0.1" min="0"
                  value={roomForm.size_sqm}
                  onChange={(e) => setRoomForm((prev) => ({ ...prev, size_sqm: e.target.value }))}
                  placeholder="z.B. 24.5"
                  data-testid="room-size-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="room-cleaning-price" className="flex items-center gap-1">
                  <Sparkles className="h-3.5 w-3.5" />
                  Reinigungspreis / Tag (€)
                </Label>
                <Input id="room-cleaning-price" type="number" step="0.01" min="0"
                  value={roomForm.cleaning_price_per_day}
                  onChange={(e) => setRoomForm((prev) => ({ ...prev, cleaning_price_per_day: e.target.value }))}
                  placeholder="z.B. 3.50"
                  data-testid="room-cleaning-price-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="room-ownership" className="flex items-center gap-1">
                  <Home className="h-3.5 w-3.5" />
                  Nachweis Räumlichkeit
                </Label>
                <Select
                  value={roomForm.ownership_type || 'none'}
                  onValueChange={(v) => setRoomForm((prev) => ({ ...prev, ownership_type: v === 'none' ? '' : v }))}
                >
                  <SelectTrigger data-testid="room-ownership-select"><SelectValue placeholder="Optional" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Keine Angabe</SelectItem>
                    <SelectItem value="owned">Eigentum</SelectItem>
                    <SelectItem value="rented">Mietobjekt</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Nachweis-Dokument Upload (nur sichtbar wenn ownership_type gesetzt) */}
            {roomForm.ownership_type && (
              <div className="space-y-2 mt-4">
                <Label>
                  {roomForm.ownership_type === 'owned' ? 'Eigentumsnachweis' : 'Mietvertrag / Nachweis'}
                  <span className="text-xs text-muted-foreground ml-1">(optional)</span>
                </Label>
                <FileDropzone
                  accept=".pdf,.jpg,.jpeg,.png"
                  onUpload={onOwnershipUpload}
                  uploading={uploadingOwnership}
                  filename={roomForm.ownership_document_filename}
                  testId="ownership-doc-dropzone"
                />
                {roomForm.ownership_document_filename && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-xs h-7"
                    onClick={() => setRoomForm((prev) => ({
                      ...prev,
                      ownership_document_url: '',
                      ownership_document_filename: '',
                    }))}
                  >
                    <X className="h-3 w-3 mr-1" />Datei entfernen
                  </Button>
                )}
              </div>
            )}
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="min-hours">Min. Buchungsdauer (Std)</Label>
                <Input id="min-hours" type="number" step="0.5" min="0.5"
                  value={roomForm.min_booking_hours}
                  onChange={(e) => setRoomForm((prev) => ({ ...prev, min_booking_hours: parseFloat(e.target.value) || 0.5 }))} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="max-hours">Max. Buchungsdauer (Std)</Label>
                <Input id="max-hours" type="number" step="0.5" min="1"
                  value={roomForm.max_booking_hours}
                  onChange={(e) => setRoomForm((prev) => ({ ...prev, max_booking_hours: parseFloat(e.target.value) || 8 }))} />
              </div>
            </div>

            <div className="flex items-center gap-3 mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <input type="checkbox" id="bookable-by-employees"
                checked={roomForm.bookable_by_employees}
                onChange={(e) => setRoomForm((prev) => ({ ...prev, bookable_by_employees: e.target.checked }))}
                className="h-4 w-4 rounded border-gray-300" data-testid="bookable-by-employees-checkbox" />
              <div>
                <Label htmlFor="bookable-by-employees" className="font-medium cursor-pointer text-blue-700 dark:text-blue-300">
                  Buchbar durch Mitarbeiter
                </Label>
                <p className="text-xs text-blue-600 dark:text-blue-400 mt-0.5">
                  Mitarbeiter am zugewiesenen Standort können diesen Raum selbst buchen
                </p>
              </div>
            </div>

            {/* Reinigung / Putzplan */}
            <div className="mt-4 rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50/60 dark:bg-emerald-900/15 p-4">
              <h4 className="font-medium text-sm mb-3 flex items-center gap-2 text-emerald-700 dark:text-emerald-300">
                <Sparkles className="h-4 w-4" />Reinigung
              </h4>
              <div className="space-y-2">
                <Label className="flex items-center gap-2">In Reinigung einbeziehen</Label>
                <div className="flex items-center gap-3 h-10">
                  <Switch
                    checked={roomForm.cleaning_enabled}
                    onCheckedChange={(v) => setRoomForm((prev) => ({ ...prev, cleaning_enabled: v }))}
                    data-testid="room-cleaning-enabled-checkbox" />
                  <span className="text-sm text-muted-foreground">
                    {roomForm.cleaning_enabled ? 'Wird im Putzplan gezählt' : 'Vom Putzplan ausgenommen'}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">
                  Der Reinigungspreis wird oben bei „Reinigungspreis / Tag" gepflegt.
                </p>
              </div>
            </div>

            {/* Individuelle Felder */}
            <div className="mt-4">
              <div className="flex items-center justify-between mb-2">
                <Label>Individuelle Felder</Label>
                <Button type="button" variant="outline" size="sm"
                  onClick={() => setRoomForm((prev) => ({
                    ...prev, custom_fields: [...prev.custom_fields, { name: '', value: '', field_type: 'text' }]
                  }))}>
                  <Plus className="h-3 w-3 mr-1" />Feld hinzufügen
                </Button>
              </div>
              {roomForm.custom_fields.length > 0 && (
                <div className="space-y-2">
                  {roomForm.custom_fields.map((field, idx) => (
                    <div key={`custom-${idx}`} className="flex gap-2 items-center">
                      <Input placeholder="Feldname" value={field.name}
                        onChange={(e) => {
                          const newFields = [...roomForm.custom_fields];
                          newFields[idx].name = e.target.value;
                          setRoomForm((prev) => ({ ...prev, custom_fields: newFields }));
                        }} className="flex-1" />
                      <Input placeholder="Wert" value={field.value}
                        onChange={(e) => {
                          const newFields = [...roomForm.custom_fields];
                          newFields[idx].value = e.target.value;
                          setRoomForm((prev) => ({ ...prev, custom_fields: newFields }));
                        }} className="flex-1" />
                      <Button type="button" variant="ghost" size="icon"
                        onClick={() => {
                          const newFields = roomForm.custom_fields.filter((_, i) => i !== idx);
                          setRoomForm((prev) => ({ ...prev, custom_fields: newFields }));
                        }}>
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-full">Abbrechen</Button>
            <Button onClick={onSave} disabled={saving} className="rounded-full" data-testid="save-room-btn">
              {saving ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Speichern...</>) : (editingRoom ? 'Aktualisieren' : 'Erstellen')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
